import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm lg:text-[15px] font-medium transition-all duration-300 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow-glow-emerald hover:bg-primary/90 hover:shadow-glow-emerald-strong hover:scale-105",
        destructive:
          "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90",
        outline:
          "border border-input bg-background shadow-sm hover:bg-accent hover:text-accent-foreground hover:border-primary/50",
        secondary:
          "bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        // Enhanced Rematch Gaming Variants
        emerald:
          "bg-gradient-to-r from-emerald-500 to-teal-500 text-white shadow-glow-emerald hover:from-emerald-600 hover:to-teal-600 hover:shadow-glow-emerald-strong hover:scale-105",
        teal:
          "bg-gradient-to-r from-teal-500 to-emerald-500 text-white shadow-glow-teal hover:from-teal-600 hover:to-emerald-600 hover:shadow-glow-teal-strong hover:scale-105",
        gaming:
          "bg-card border border-primary/20 text-foreground shadow-gaming-card hover:border-primary/50 hover:shadow-gaming-card-hover hover:scale-105",
        "gaming-outline":
          "border-2 border-primary/50 bg-transparent text-primary hover:bg-primary/10 hover:border-primary hover:shadow-glow-emerald",
        premium:
          "bg-gradient-to-r from-emerald-500 via-teal-500 to-emerald-500 text-white shadow-glow-emerald-strong border-gradient-animated hover:scale-105",
        neumorphic:
          "bg-neu-brand text-neu-text-on-brand shadow-neumorphic rounded-[16px] hover:scale-105 transition-transform font-montserrat font-bold",
        "neumorphic-secondary":
          "bg-neu-surface text-neu-text-primary shadow-neumorphic rounded-[16px] hover:scale-105 transition-transform font-montserrat font-bold",
      },
      size: {
        default: "h-9 lg:h-10 px-4 py-2",
        sm: "h-8 rounded-md px-3 text-xs",
        lg: "h-10 lg:h-11 xl:h-12 rounded-md px-8",
        icon: "h-9 w-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
